import { ExpiredIcon } from '@/assets/icons/expired';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { SEND_RESET_PASSWORD_EMAIL_MUTATION } from '../queries';
import { IUser } from '../types';

interface SendResetPasswordEmailProps {
  isOpen: boolean;
  currentUser: IUser | null;
  onClose: () => void;
}

const SendResetPasswordEmailDialog = ({ isOpen, currentUser, onClose }: SendResetPasswordEmailProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [sendResetPasswordEmail, { loading: sendResetPasswordEmailLoading }] = useMutation(SEND_RESET_PASSWORD_EMAIL_MUTATION);

  const onSave = () => {
    setErrorMessage(null);

    sendResetPasswordEmail({
      variables: {
        email: currentUser?.email
      },
      onCompleted: () => {
        onClose();
      },
      onError: () => {
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  if (!currentUser) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Send Email',
        onClick: onSave,
        disabled: sendResetPasswordEmailLoading
      }}
      showCloseIcon={!sendResetPasswordEmailLoading}
      showCancelButton={!sendResetPasswordEmailLoading}
      isUseOutsideClick={!sendResetPasswordEmailLoading}
    >
      <ModalBody>
        <ExpiredIcon width={108} height={55} />
        <ModalTitle>Send Reset Password Email</ModalTitle>
        {sendResetPasswordEmailLoading ? (
          <ModalLoader />
        ) : (
          <>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>Send password reset instructions to this user’s email address.</ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default SendResetPasswordEmailDialog;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;
